import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  X, Settings, MapPin, Globe, Shield, FileText, ArrowRight, Lock, 
  Eye, EyeOff, Smartphone, Building2, Receipt, Link2, ExternalLink,
  CheckCircle2, XCircle, Clock, Monitor, AlertTriangle, Copy, RefreshCw,
  BookOpen, Download, Users, Trash2, Plus, Star, Loader2, Unlink, Package, Megaphone, Palette
} from 'lucide-react';
import LocationsSettings from '../components/LocationsSettings';
import AdminBrandingSettings from './admin/AdminBrandingSettings';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { isAdmin, apiCall, user } = useAuth();
  const { toast } = useToast();
  
  const [activeSection, setActiveSection] = useState('platform-config');
  
  // Set initial section based on role after component mounts
  useEffect(() => {
    if (!isAdmin()) {
      setActiveSection('overview');
    }
  }, [isAdmin]);

  // Fetch domains on mount
  useEffect(() => {
    if (isAdmin()) {
      fetchDomains();
    }
  }, [isAdmin]);

  // Domain fetch function
  const fetchDomains = async () => {
    setDomainsLoading(true);
    try {
      const response = await apiCall('/api/domain-settings/');
      setDomains(response || []);
    } catch (error) {
      console.error('Error fetching domains:', error);
    } finally {
      setDomainsLoading(false);
    }
  };

  // Add domain function
  const handleAddDomain = async () => {
    if (!domainConfig.custom_domain) {
      toast({ title: 'Error', description: 'Please enter a domain name', variant: 'destructive' });
      return;
    }

    setDomainSaving(true);
    try {
      await apiCall('/api/domain-settings/', {
        method: 'POST',
        body: JSON.stringify({
          domain: domainConfig.custom_domain,
          domain_type: 'storefront',
          is_primary: false
        })
      });
      
      toast({ title: 'Success', description: 'Domain added successfully. Configure DNS to verify.' });
      setDomainConfig(prev => ({ ...prev, custom_domain: '' }));
      fetchDomains();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to add domain', variant: 'destructive' });
    } finally {
      setDomainSaving(false);
    }
  };

  // Verify domain function
  const handleVerifyDomain = async (domainId) => {
    setVerifyingDomain(domainId);
    try {
      const response = await apiCall(`/api/domain-settings/${domainId}/verify`, { method: 'POST' });
      toast({ title: 'Verification Started', description: response.message });
      fetchDomains();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Verification failed', variant: 'destructive' });
    } finally {
      setVerifyingDomain(null);
    }
  };

  // Simulate verify (for demo)
  const handleSimulateVerify = async (domainId) => {
    setVerifyingDomain(domainId);
    try {
      await apiCall(`/api/domain-settings/${domainId}/simulate-verify`, { method: 'POST' });
      toast({ title: 'Success', description: 'Domain verified successfully' });
      fetchDomains();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Verification failed', variant: 'destructive' });
    } finally {
      setVerifyingDomain(null);
    }
  };

  // Delete domain function
  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Are you sure you want to remove this domain?')) return;
    
    try {
      await apiCall(`/api/domain-settings/${domainId}`, { method: 'DELETE' });
      toast({ title: 'Success', description: 'Domain removed successfully' });
      fetchDomains();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to delete domain', variant: 'destructive' });
    }
  };
  
  // Platform Config state
  const [platformConfig, setPlatformConfig] = useState({
    business_name: '',
    legal_name: '',
    billing_email: '',
    support_email: '',
    phone: '',
    gst_number: '',
    pan_number: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    invoice_prefix: 'INV',
    invoice_footer: '',
    vendor_invoice_format: 'standard'
  });
  
  // Domain state
  const [domainConfig, setDomainConfig] = useState({
    primary_domain: '',
    custom_domain: '',
    ssl_enabled: true,
    domain_status: 'pending'
  });
  
  // Domain management state
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [domainSaving, setDomainSaving] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(null);
  
  // Security state
  const [securityData, setSecurityData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
    two_factor_enabled: false
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [securityLoading, setSecurityLoading] = useState(false);
  
  // Active sessions mock data
  const [activeSessions] = useState([
    { id: 1, device: 'Chrome on Windows', location: 'Mumbai, India', lastActive: 'Active now', current: true },
    { id: 2, device: 'Safari on iPhone', location: 'Delhi, India', lastActive: '2 hours ago', current: false },
    { id: 3, device: 'Firefox on MacOS', location: 'Bangalore, India', lastActive: '1 day ago', current: false }
  ]);
  
  // Zoho Integration state
  const [zohoStatus, setZohoStatus] = useState(null);
  const [zohoLoading, setZohoLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  
  // Fetch Zoho status on mount for vendors
  useEffect(() => {
    if (!isAdmin()) {
      fetchZohoStatus();
      
      // Check URL params for Zoho callback status
      const params = new URLSearchParams(window.location.search);
      if (params.get('zoho_connected') === 'true') {
        toast({
          title: 'Zoho Invoice Connected',
          description: 'Your Zoho Invoice account has been successfully connected!'
        });
        fetchZohoStatus();
        window.history.replaceState({}, '', window.location.pathname);
      } else if (params.get('zoho_error')) {
        const zohoErr = params.get('zoho_error');
        const errorMessages = {
          'no_refresh_token': 'Zoho did not provide full access. Please try again and click "Accept" on the consent page.',
          'no_access_token': 'Zoho authorization failed — no access token received. Check your Client ID/Secret.',
          'no_organization': 'No Zoho organization found. Make sure you have at least one Zoho Invoice organization.',
          'connection_failed': 'Connection failed. Check your Zoho API credentials in Integration Settings.',
          'invalid_client': 'Invalid Zoho Client ID or Secret. Update in Integration Settings → Zoho Invoice.',
          'access_denied': 'Access was denied. Please try again and grant access on the Zoho consent page.',
        };
        toast({
          title: 'Zoho Connection Failed',
          description: errorMessages[zohoErr] || `Error: ${zohoErr}`,
          variant: 'destructive'
        });
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [isAdmin]);
  
  const fetchZohoStatus = async () => {
    try {
      setZohoLoading(true);
      const data = await apiCall('/api/auth/zoho/status');
      setZohoStatus(data);
    } catch (error) {
      console.error('Failed to fetch Zoho status:', error);
      setZohoStatus({ is_connected: false });
    } finally {
      setZohoLoading(false);
    }
  };
  
  const handleConnectZoho = async () => {
    try {
      setZohoLoading(true);
      const data = await apiCall('/api/auth/zoho/authorize');
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start Zoho connection',
        variant: 'destructive'
      });
      setZohoLoading(false);
    }
  };
  
  const handleDisconnectZoho = async () => {
    if (!window.confirm('Are you sure you want to disconnect Zoho Invoice? You will no longer be able to automatically generate invoices.')) {
      return;
    }
    
    try {
      setDisconnecting(true);
      await apiCall('/api/auth/zoho/disconnect', { method: 'POST' });
      toast({
        title: 'Disconnected',
        description: 'Zoho Invoice has been disconnected'
      });
      setZohoStatus({ is_connected: false });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect Zoho Invoice',
        variant: 'destructive'
      });
    } finally {
      setDisconnecting(false);
    }
  };

  // Admin sections - Streamlined 6 items
  const adminSections = [
    { id: 'platform-config', label: 'Platform Config', icon: Settings },
    { id: 'domains', label: 'Domains', icon: Globe },
    { id: 'locations', label: 'Locations', icon: MapPin },
    { id: 'look-and-feel', label: 'Look & Feel', icon: Palette },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'privacy-policies', label: 'Privacy & Policies', icon: FileText },
    { id: 'documentation', label: 'System Documentation', icon: BookOpen }
  ];

  // Vendor sections - simplified with cards
  const vendorSections = [
    { id: 'overview', label: 'Settings Overview', icon: Settings },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'locations', label: 'Locations', icon: MapPin }
  ];

  const settingsSections = isAdmin() ? adminSections : vendorSections;

  // Handle password change
  const handlePasswordChange = async () => {
    if (securityData.new_password !== securityData.confirm_password) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        variant: 'destructive'
      });
      return;
    }
    
    if (securityData.new_password.length < 8) {
      toast({
        title: 'Error',
        description: 'Password must be at least 8 characters',
        variant: 'destructive'
      });
      return;
    }
    
    setSecurityLoading(true);
    try {
      await apiCall('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: securityData.current_password,
          new_password: securityData.new_password
        })
      });
      toast({
        title: 'Success',
        description: 'Password changed successfully'
      });
      setSecurityData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to change password',
        variant: 'destructive'
      });
    } finally {
      setSecurityLoading(false);
    }
  };

  // Platform Config Section
  const renderPlatformConfig = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Platform Configuration</h2>
        <p className="text-gray-500 mt-1">Manage your marketplace identity, billing details, and invoice settings</p>
      </div>

      <Tabs defaultValue="merchant" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="merchant">Merchant Info</TabsTrigger>
          <TabsTrigger value="billing">Billing Details</TabsTrigger>
          <TabsTrigger value="invoice">Invoice Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="merchant" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Merchant Account Details
              </CardTitle>
              <CardDescription>Primary business information displayed across the marketplace</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    value={platformConfig.business_name}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, business_name: e.target.value }))}
                    placeholder="Your Marketplace Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="legal_name">Legal Entity Name *</Label>
                  <Input
                    id="legal_name"
                    value={platformConfig.legal_name}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, legal_name: e.target.value }))}
                    placeholder="Legal company name for invoices"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="support_email">Support Email</Label>
                  <Input
                    id="support_email"
                    type="email"
                    value={platformConfig.support_email}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, support_email: e.target.value }))}
                    placeholder="support@yourmarketplace.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Contact Phone</Label>
                  <Input
                    id="phone"
                    value={platformConfig.phone}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address">Registered Address</Label>
                <Textarea
                  id="address"
                  value={platformConfig.address}
                  onChange={(e) => setPlatformConfig(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Full registered address"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={platformConfig.city}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Mumbai"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={platformConfig.state}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="Maharashtra"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pincode">PIN Code</Label>
                  <Input
                    id="pincode"
                    value={platformConfig.pincode}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, pincode: e.target.value }))}
                    placeholder="400001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={platformConfig.country}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, country: e.target.value }))}
                    placeholder="India"
                  />
                </div>
              </div>
              
              <Button className="mt-4">Save Merchant Details</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Billing & Tax Information
              </CardTitle>
              <CardDescription>Tax registration details for invoices and compliance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billing_email">Billing Email</Label>
                  <Input
                    id="billing_email"
                    type="email"
                    value={platformConfig.billing_email}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, billing_email: e.target.value }))}
                    placeholder="billing@yourmarketplace.com"
                  />
                  <p className="text-xs text-gray-500">Invoices and billing notifications will be sent here</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gst_number">GST Number</Label>
                  <Input
                    id="gst_number"
                    value={platformConfig.gst_number}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, gst_number: e.target.value }))}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pan_number">PAN Number</Label>
                  <Input
                    id="pan_number"
                    value={platformConfig.pan_number}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, pan_number: e.target.value }))}
                    placeholder="AAAAA0000A"
                  />
                </div>
              </div>
              
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Tax Compliance Note</h4>
                <p className="text-sm text-blue-800">
                  Ensure your GST and PAN details are accurate. These will appear on all marketplace invoices and are required for tax compliance.
                </p>
              </div>
              
              <Button className="mt-4">Save Billing Details</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoice" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Invoice Configuration
              </CardTitle>
              <CardDescription>Customize invoice formatting and vendor invoice settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="invoice_prefix">Invoice Number Prefix</Label>
                  <Input
                    id="invoice_prefix"
                    value={platformConfig.invoice_prefix}
                    onChange={(e) => setPlatformConfig(prev => ({ ...prev, invoice_prefix: e.target.value }))}
                    placeholder="INV"
                  />
                  <p className="text-xs text-gray-500">Example: INV-2024-0001</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_invoice_format">Vendor Invoice Format</Label>
                  <Select 
                    value={platformConfig.vendor_invoice_format}
                    onValueChange={(value) => setPlatformConfig(prev => ({ ...prev, vendor_invoice_format: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard Format</SelectItem>
                      <SelectItem value="detailed">Detailed with Line Items</SelectItem>
                      <SelectItem value="compact">Compact Summary</SelectItem>
                      <SelectItem value="gst_compliant">GST Compliant Format</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="invoice_footer">Invoice Footer Text</Label>
                <Textarea
                  id="invoice_footer"
                  value={platformConfig.invoice_footer}
                  onChange={(e) => setPlatformConfig(prev => ({ ...prev, invoice_footer: e.target.value }))}
                  placeholder="Thank you for your business! For queries, contact support@yourmarketplace.com"
                  rows={3}
                />
                <p className="text-xs text-gray-500">This text will appear at the bottom of all invoices</p>
              </div>
              
              <div className="border-t pt-6">
                <h4 className="font-semibold mb-4">Individual Vendor Invoice Settings</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Allow Vendor Custom Branding</p>
                      <p className="text-sm text-gray-500">Let vendors add their logo to invoices</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Include Marketplace Header</p>
                      <p className="text-sm text-gray-500">Show marketplace branding on vendor invoices</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Auto-generate Vendor Invoices</p>
                      <p className="text-sm text-gray-500">Automatically create invoices for vendor payouts</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </div>
              </div>
              
              <Button className="mt-4">Save Invoice Settings</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );

  // Domains Section
  const renderDomainsSection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Domains</h2>
          <p className="text-gray-500 mt-1">Configure your marketplace domain and SSL settings</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchDomains} disabled={domainsLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${domainsLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Default Platform Domain */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Platform Domain
          </CardTitle>
          <CardDescription>Your marketplace&apos;s default access URL</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div className="flex-1">
              <p className="font-medium text-green-900">marketplace-tools.emergent.host</p>
              <p className="text-sm text-green-700">Default platform domain - Always active</p>
            </div>
            <Badge className="bg-green-500">Active</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Add Custom Domain */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Custom Domain
          </CardTitle>
          <CardDescription>Connect your own domain to the marketplace</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="custom_domain">Domain Name</Label>
            <div className="flex gap-2">
              <Input
                id="custom_domain"
                value={domainConfig.custom_domain}
                onChange={(e) => setDomainConfig(prev => ({ ...prev, custom_domain: e.target.value.toLowerCase() }))}
                placeholder="shop.example.com"
                className="flex-1"
              />
              <Button onClick={handleAddDomain} disabled={domainSaving || !domainConfig.custom_domain}>
                {domainSaving ? 'Adding...' : 'Add Domain'}
              </Button>
            </div>
            <p className="text-xs text-gray-500">Enter your domain without http:// or https://</p>
          </div>
        </CardContent>
      </Card>

      {/* Connected Domains List */}
      {domains.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Connected Domains ({domains.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {domains.map((domain) => (
              <div key={domain.id} className={`p-4 border rounded-lg ${!domain.is_active ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold">{domain.domain}</span>
                      {domain.is_primary && (
                        <Badge className="bg-amber-100 text-amber-800">
                          <Star className="h-3 w-3 mr-1" /> Primary
                        </Badge>
                      )}
                      <Badge variant="outline">{domain.domain_type}</Badge>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {/* DNS Status */}
                      {domain.dns_status === 'verified' ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> DNS Verified
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800">
                          <Clock className="h-3 w-3 mr-1" /> DNS Pending
                        </Badge>
                      )}
                      
                      {/* SSL Status */}
                      {domain.ssl_status === 'active' ? (
                        <Badge className="bg-green-100 text-green-800">
                          <Shield className="h-3 w-3 mr-1" /> SSL Active
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800">
                          <Shield className="h-3 w-3 mr-1" /> SSL Pending
                        </Badge>
                      )}
                    </div>

                    {/* DNS Instructions for unverified domains */}
                    {domain.dns_status !== 'verified' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                        <p className="text-sm text-amber-800 font-medium mb-2">Add this TXT record to verify ownership:</p>
                        <div className="space-y-2 text-xs">
                          <div className="flex items-center gap-2 bg-white p-2 rounded border font-mono">
                            <span className="text-gray-600">Host:</span>
                            <span className="flex-1">_marketplace-verify.{domain.domain}</span>
                            <button onClick={() => {
                              navigator.clipboard.writeText(`_marketplace-verify.${domain.domain}`);
                              toast({ title: 'Copied!' });
                            }}>
                              <Copy className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 bg-white p-2 rounded border font-mono">
                            <span className="text-gray-600">Value:</span>
                            <span className="flex-1 truncate">{domain.verification_token}</span>
                            <button onClick={() => {
                              navigator.clipboard.writeText(domain.verification_token);
                              toast({ title: 'Copied!' });
                            }}>
                              <Copy className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-gray-500">
                      Added {new Date(domain.created_at).toLocaleDateString()}
                      {domain.verified_at && ` • Verified ${new Date(domain.verified_at).toLocaleDateString()}`}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    {domain.dns_status !== 'verified' ? (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleVerifyDomain(domain.id)}
                          disabled={verifyingDomain === domain.id}
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${verifyingDomain === domain.id ? 'animate-spin' : ''}`} />
                          Check DNS
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSimulateVerify(domain.id)}
                          disabled={verifyingDomain === domain.id}
                          className="text-green-600 border-green-200"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Verify (Demo)
                        </Button>
                      </>
                    ) : null}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDeleteDomain(domain.id)}
                      className="text-red-600 border-red-200"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* SSL Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            SSL Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium">Auto-provision SSL</p>
                <p className="text-sm text-gray-500">Free SSL certificate via Let&apos;s Encrypt for verified domains</p>
              </div>
            </div>
            <Switch 
              checked={domainConfig.ssl_enabled}
              onCheckedChange={(checked) => setDomainConfig(prev => ({ ...prev, ssl_enabled: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* How it works */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-medium text-blue-900 mb-3">How Domain Connection Works</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-blue-900">Add Domain</p>
                <p className="text-blue-700">Enter your custom domain name</p>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-blue-900">Configure DNS</p>
                <p className="text-blue-700">Add the TXT record to verify ownership</p>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
              <div>
                <p className="font-medium text-blue-900">Verify & Go Live</p>
                <p className="text-blue-700">SSL is auto-provisioned after verification</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Security Section with Active Users
  const renderSecuritySection = () => (
    <div className="space-y-6">
      {!isAdmin() && (
        <Button variant="ghost" className="mb-2" onClick={() => setActiveSection('overview')}>
          <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
          Back to Overview
        </Button>
      )}
      <div>
        <h2 className="text-2xl font-bold">Security</h2>
        <p className="text-gray-500 mt-1">Manage passwords, authentication, and active sessions</p>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Current Password</Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showPasswords.current ? 'text' : 'password'}
                value={securityData.current_password}
                onChange={(e) => setSecurityData(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder="Enter current password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
              >
                {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPasswords.new ? 'text' : 'password'}
                value={securityData.new_password}
                onChange={(e) => setSecurityData(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="Enter new password (min 8 characters)"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
              >
                {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={securityData.confirm_password}
                onChange={(e) => setSecurityData(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
              >
                {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <Button onClick={handlePasswordChange} disabled={securityLoading}>
            {securityLoading ? 'Updating...' : 'Update Password'}
          </Button>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>Add an extra layer of security to your account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <p className="font-medium">Enable 2FA</p>
              <p className="text-sm text-gray-500">Require a verification code when logging in</p>
            </div>
            <Switch
              checked={securityData.two_factor_enabled}
              onCheckedChange={(checked) => {
                setSecurityData(prev => ({ ...prev, two_factor_enabled: checked }));
                toast({
                  title: checked ? '2FA Enabled' : '2FA Disabled',
                  description: checked ? 'Two-factor authentication is now active' : 'Two-factor authentication has been turned off'
                });
              }}
            />
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Active Sessions
          </CardTitle>
          <CardDescription>Manage devices where you or team members are logged in</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activeSessions.map((session) => (
              <div 
                key={session.id} 
                className={`flex items-center justify-between p-4 border rounded-lg ${session.current ? 'bg-green-50 border-green-200' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded ${session.current ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Monitor className={`h-4 w-4 ${session.current ? 'text-green-600' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <p className="font-medium">{session.device}</p>
                    <p className="text-xs text-gray-500">{session.location} - {session.lastActive}</p>
                  </div>
                </div>
                {session.current ? (
                  <Badge className="bg-green-500">Current</Badge>
                ) : (
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    Revoke
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button variant="outline" className="mt-4 text-red-600 border-red-200 hover:bg-red-50">
            Sign out all other sessions
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // Privacy & Policies Section (Merged)
  const renderPrivacyPoliciesSection = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Privacy & Policies</h2>
        <p className="text-gray-500 mt-1">Manage data collection settings and legal policies</p>
      </div>

      <Tabs defaultValue="privacy" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="privacy">Privacy Settings</TabsTrigger>
          <TabsTrigger value="policies">Legal Policies</TabsTrigger>
        </TabsList>

        <TabsContent value="privacy" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Data Collection</CardTitle>
              <CardDescription>Configure what data is collected from customers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Marketing Tracking</p>
                  <p className="text-sm text-gray-500">Track customer behavior for marketing analytics</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Third-party Analytics</p>
                  <p className="text-sm text-gray-500">Allow third-party analytics tools (Google Analytics, etc.)</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Personalization Data</p>
                  <p className="text-sm text-gray-500">Collect data for personalized recommendations</p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Cookie Consent</CardTitle>
              <CardDescription>Manage cookie consent banner settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Show Cookie Banner</p>
                  <p className="text-sm text-gray-500">Display cookie consent banner to visitors</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <div className="space-y-2">
                <Label>Cookie Policy Link</Label>
                <Input placeholder="https://yourstore.com/cookie-policy" />
              </div>
              
              <Button>Save Privacy Settings</Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Data Retention</CardTitle>
              <CardDescription>Configure how long customer data is retained</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Order Data Retention</Label>
                <Select defaultValue="forever">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1year">1 Year</SelectItem>
                    <SelectItem value="3years">3 Years</SelectItem>
                    <SelectItem value="5years">5 Years</SelectItem>
                    <SelectItem value="forever">Keep Forever</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Customer Account Data Retention</Label>
                <Select defaultValue="forever">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inactive1year">Delete after 1 year of inactivity</SelectItem>
                    <SelectItem value="inactive3years">Delete after 3 years of inactivity</SelectItem>
                    <SelectItem value="forever">Keep Forever</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button>Save Retention Settings</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="policies" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Refund Policy</CardTitle>
              <CardDescription>Define your store&apos;s refund and return policy</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Enter your refund policy..."
                className="min-h-[150px]"
                defaultValue="We offer a 30-day return policy for all unused items in their original packaging. Refunds will be processed within 5-7 business days after we receive the returned item."
              />
              <Button className="mt-4">Save Policy</Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Privacy Policy</CardTitle>
              <CardDescription>Your store&apos;s privacy policy</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Enter your privacy policy..."
                className="min-h-[150px]"
              />
              <Button className="mt-4">Save Policy</Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Terms of Service</CardTitle>
              <CardDescription>Terms and conditions for using your store</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Enter your terms of service..."
                className="min-h-[150px]"
              />
              <Button className="mt-4">Save Policy</Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Shipping Policy</CardTitle>
              <CardDescription>Shipping terms and delivery information</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Enter your shipping policy..."
                className="min-h-[150px]"
              />
              <Button className="mt-4">Save Policy</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );

  // System Documentation Section
  const renderDocumentationSection = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">System Documentation</h2>
        <p className="text-gray-500 mt-1">Access technical documentation, epics, user stories, and feature specifications</p>
      </div>

      {/* Primary Documentation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Documentation Hub
          </CardTitle>
          <CardDescription>All system documentation in one place - reference these before making any changes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* System Architecture - Primary Reference */}
          <div className="p-6 border-2 border-purple-200 rounded-lg bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-lg">System Architecture</h3>
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">PRIMARY</span>
                </div>
                <p className="text-sm text-gray-700 mb-3">
                  Complete documentation with <strong>465+ user stories</strong> across <strong>24 Epics</strong> (19 Admin + 5 Vendor). 
                  Includes features, APIs, collections, and technical architecture.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/system/architecture`, '_blank')}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                    size="sm"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Architecture
                  </Button>
                  <Button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `${process.env.REACT_APP_BACKEND_URL}/api/system/architecture/download`;
                      link.download = 'SYSTEM_ARCHITECTURE.md';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download MD
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Documentation Links Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Feature Truth (SSOT) */}
            <Card className="border-2 hover:border-emerald-300 transition-colors cursor-pointer"
                  onClick={() => window.open(`${window.location.origin}/feature-truth.html#overview`, '_blank')}
                  data-testid="doc-card-feature-truth">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h4 className="font-semibold">Feature Truth (SSOT)</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">15 modules — the Single Source of Truth for business rules and workflow gates</p>
                <div className="flex gap-1 flex-wrap">
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded">Vendor · Subscriptions · Orders</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded">Shipping · Payouts · GST</span>
                </div>
              </CardContent>
            </Card>

            {/* Cleanup Master Plan */}
            <Card className="border-2 hover:border-indigo-300 transition-colors cursor-pointer"
                  onClick={() => window.open(`${window.location.origin}/master-plan.html`, '_blank')}
                  data-testid="doc-card-master-plan">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-indigo-600" />
                  </div>
                  <h4 className="font-semibold">Cleanup Master Plan</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">7-phase code + DB cleanup roadmap with GAP analysis</p>
                <div className="flex gap-1 flex-wrap">
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded">Route consolidation</span>
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded">DB rationalization</span>
                </div>
              </CardContent>
            </Card>

            {/* Feature Matrix */}
            <Card className="border-2 hover:border-green-300 transition-colors cursor-pointer" 
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/system/features`, '_blank')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-green-600" />
                  </div>
                  <h4 className="font-semibold">Feature Validation Matrix</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">430+ features with status, frontend/backend references</p>
                <div className="flex gap-1 flex-wrap">
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">✅ Active</span>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">⚠️ Ghost UI</span>
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">🔴 Redundant</span>
                </div>
              </CardContent>
            </Card>

            {/* PRD */}
            <Card className="border-2 hover:border-blue-300 transition-colors cursor-pointer"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/system/prd`, '_blank')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <h4 className="font-semibold">Product Requirements (PRD)</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">Problem statement, session changelog, next actions</p>
                <div className="flex gap-1">
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Session History</span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Action Items</span>
                </div>
              </CardContent>
            </Card>

            {/* System Manual */}
            <Card className="border-2 hover:border-amber-300 transition-colors cursor-pointer"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/system/manual`, '_blank')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-amber-600" />
                  </div>
                  <h4 className="font-semibold">System Manual</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">Business logic, commission rules, GST/TCS/TDS calculations</p>
                <div className="flex gap-1">
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">Business Rules</span>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">Financials</span>
                </div>
              </CardContent>
            </Card>

            {/* Database Report */}
            <Card className="border-2 hover:border-slate-300 transition-colors cursor-pointer"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/system/database-report`, '_blank')}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-slate-600" />
                  </div>
                  <h4 className="font-semibold">Database Optimization</h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">107 collections, indexes, production recommendations</p>
                <div className="flex gap-1">
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded">marketplace_prod</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Reference */}
          <Card className="bg-slate-50">
            <CardContent className="p-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Before Making Changes
              </h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-gray-700 mb-2">Modifying Features:</p>
                  <ol className="list-decimal list-inside text-gray-600 space-y-1">
                    <li>Read SYSTEM_ARCHITECTURE.md for context</li>
                    <li>Check feature_validation_matrix.csv for status</li>
                    <li>Review PRD.md for recent changes</li>
                  </ol>
                </div>
                <div>
                  <p className="font-medium text-gray-700 mb-2">Adding New Features:</p>
                  <ol className="list-decimal list-inside text-gray-600 space-y-1">
                    <li>Check if feature exists in matrix</li>
                    <li>Identify relevant Epic</li>
                    <li>Follow existing patterns</li>
                    <li>Update docs after implementation</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* API Endpoint */}
          <div className="p-4 bg-gray-900 rounded-lg text-sm">
            <p className="text-gray-400 mb-2">Documentation API Endpoint:</p>
            <code className="text-green-400">GET {process.env.REACT_APP_BACKEND_URL}/api/system/docs</code>
            <p className="text-gray-500 mt-2 text-xs">Returns JSON index of all documentation with metadata</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Integrations Section for Vendors
  const renderIntegrationsSection = () => (
    <div className="space-y-6">
      <Button variant="ghost" className="mb-4" onClick={() => setActiveSection('overview')}>
        <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
        Back to Overview
      </Button>
      
      <div>
        <h2 className="text-2xl font-bold">Integrations</h2>
        <p className="text-gray-500 mt-2">Connect third-party services to enhance your business</p>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Zoho Invoice Integration</CardTitle>
          <CardDescription>
            Connect your free Zoho Invoice account to automatically generate GST-compliant invoices for your sales
          </CardDescription>
        </CardHeader>
        <CardContent>
          {zohoLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading...</span>
            </div>
          ) : zohoStatus?.is_connected ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-900">✅ Connected to Zoho Invoice</p>
                    <p className="text-sm text-green-700">
                      Organization: {zohoStatus.organization_name || 'Connected'}
                    </p>
                    {zohoStatus.connected_at && (
                      <p className="text-xs text-green-600">
                        Connected on: {new Date(zohoStatus.connected_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                <Button 
                  variant="outline"
                  onClick={handleDisconnectZoho}
                  disabled={disconnecting}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  {disconnecting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Unlink className="h-4 w-4 mr-2" />
                  )}
                  Disconnect
                </Button>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>How it works:</strong> When you receive an order, you can generate a GST-compliant invoice 
                  directly in your Zoho Invoice account. Use the &quot;Print Invoice&quot; button on any order to download the PDF.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Zoho Invoice</p>
                    <p className="text-sm text-gray-600">
                      Free GST invoicing software for Indian businesses
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleConnectZoho}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Link2 className="h-4 w-4 mr-2" />
                  Connect
                </Button>
              </div>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-900">
                  <strong>Note:</strong> You need a free Zoho Invoice account. 
                  <a 
                    href="https://www.zoho.com/invoice/signup/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline ml-1"
                  >
                    Sign up for free →
                  </a>
                </p>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>Benefits of connecting:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Automatic GST-compliant invoice generation</li>
                  <li>Professional PDF invoices with your branding</li>
                  <li>Easy GST return filing with Zoho</li>
                  <li>Customer management synced from orders</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>More Integrations Coming Soon</CardTitle>
          <CardDescription>
            We&apos;re working on more integrations to help you run your business
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border border-gray-200 rounded-lg opacity-60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Package className="h-5 w-5 text-gray-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-700">Shipping Partners</p>
                  <Badge variant="outline" className="text-xs">Coming Soon</Badge>
                </div>
              </div>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg opacity-60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Megaphone className="h-5 w-5 text-gray-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-700">WhatsApp Business</p>
                  <Badge variant="outline" className="text-xs">Coming Soon</Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Vendor Overview with Cards
  const renderVendorOverview = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Settings</h2>
        <p className="text-gray-500 mt-2">Manage your account settings and preferences</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveSection('security')}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">Security</h3>
                <p className="text-sm text-gray-500 mt-1">Password, 2FA, and login settings</p>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveSection('locations')}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-rose-100 rounded-lg">
                <MapPin className="h-6 w-6 text-rose-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">Locations</h3>
                <p className="text-sm text-gray-500 mt-1">Business locations and addresses</p>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/vendor/store-profile')}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Building2 className="h-6 w-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">Store Profile</h3>
                <p className="text-sm text-gray-500 mt-1">Business info and branding</p>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveSection('integrations')}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Link2 className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">Integrations</h3>
                <p className="text-sm text-gray-500 mt-1">Connect Zoho Invoice and more</p>
                {zohoStatus?.is_connected && (
                  <Badge variant="outline" className="mt-1 text-green-600 border-green-300 text-xs">
                    Zoho Connected
                  </Badge>
                )}
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderContent = () => {
    // For vendors, show overview first
    if (!isAdmin() && activeSection === 'overview') {
      return renderVendorOverview();
    }
    
    switch (activeSection) {
      case 'platform-config':
        return renderPlatformConfig();
      
      case 'domains':
        return renderDomainsSection();
      
      case 'locations':
        return (
          <div className="space-y-6">
            {!isAdmin() && (
              <Button variant="ghost" className="mb-4" onClick={() => setActiveSection('overview')}>
                <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
                Back to Overview
              </Button>
            )}
            <LocationsSettings />
          </div>
        );
      
      case 'look-and-feel':
        return (
          <div className="space-y-6">
            <AdminBrandingSettings embedded={true} />
          </div>
        );
      
      case 'security':
        return renderSecuritySection();
      
      case 'privacy-policies':
        return renderPrivacyPoliciesSection();
      
      case 'documentation':
        return renderDocumentationSection();
      
      case 'integrations':
        return renderIntegrationsSection();

      default:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold">{settingsSections.find(s => s.id === activeSection)?.label || activeSection}</h2>
              <p className="text-gray-500 mt-2">Settings for this section</p>
            </div>
            <Card>
              <CardContent className="pt-6">
                <p className="text-gray-600">Content for {activeSection} coming soon...</p>
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Settings Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-bold">Settings</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="h-8 w-8 p-0"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-1">
            {settingsSections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <li key={section.id}>
                  <button
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition-all ${
                      isActive
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{section.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Settings Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-8 max-w-5xl mx-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
